import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Clock, AlertTriangle, Loader2, Truck, Search, RotateCcw } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { gbp } from "@/lib/pricing";
import { useCart } from "@/context/CartContext";
import { usePartner } from "@/context/PartnerContext";
import { rebuildOrderItems } from "@/pages/Reorder";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SupportBox } from "@/components/SupportLinks";
import { useCopy } from "@/lib/copy";

const STATUS_LABEL = {
  awaiting_payment: "Awaiting Payment",
  confirming: "Confirming on-chain…",
  paid: "Payment Received",
  failed: "Payment Failed",
};
const FULFILMENT_LABEL = {
  pending: "Preparing your order",
  packed: "Packed — awaiting dispatch",
  shipped: "Dispatched",
  delivered: "Delivered",
};

const orderItemLabel = (item) => {
  if (item.type === "single" || item.type === "offer") {
    return `${item.quantity}× ${item.flavor_name} — ${item.size_label} (${item.base})`;
  }
  if (item.type === "multipack") return `${item.quantity}× ${item.multipack_name || "Multipack"}`;
  if (item.type === "guest") {
    const opts = (item.options || []).map((o) => o.label).filter(Boolean).join(", ");
    return `${item.quantity}× ${item.flavor_name}${item.size_label ? ` — ${item.size_label}` : ""}${opts ? ` (${opts})` : ""}`;
  }
  return `${item.bottle_count || 0}× ${item.size_label || "Bottle"} box`;
};

export default function TrackOrder() {
  const nav = useNavigate();
  const { clear, addItem } = useCart();
  const { partner } = usePartner();
  const { t, C } = useCopy();
  const [orderNumber, setOrderNumber] = useState("");
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [order, setOrder] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!orderNumber.trim() || !pin.trim()) { toast.error("Enter both your Order ID and Tracking PIN"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/orders/track", { order_number: orderNumber.trim(), pin: pin.trim() });
      setOrder(data);
    } catch (err) {
      setOrder(null);
      toast.error(apiError(err));
    } finally { setLoading(false); }
  };

  const reorder = async () => {
    if (!order) return;
    setReordering(true);
    try {
      const [f, s, m] = await Promise.all([api.get("/flavors"), api.get("/sizes"), api.get("/multipacks")]);
      const { items, skipped } = rebuildOrderItems(order, { flavors: f.data, sizes: s.data, multipacks: m.data });
      if (!items.length) { toast.error("None of the items from this order are available any more."); return; }
      clear();
      items.forEach(addItem);
      toast.success("Added to your basket at today's prices");
      if (skipped.length) toast.info(`Not available any more: ${skipped.join(", ")}`);
      nav("/cart");
    } catch {
      toast.error("Couldn't rebuild this order. Please try again.");
    } finally { setReordering(false); }
  };

  const paid = order?.payment_status === "paid";
  const failed = order?.payment_status === "failed";
  const confirming = order?.payment_status === "confirming";

  return (
    <div className="mx-auto max-w-2xl px-6 py-16">
      <div className="mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground">{t("track_eyebrow")}</div>
      <h1 className="mt-3 font-heading text-4xl font-black tracking-tighter sm:text-5xl">{t("track_title")}</h1>
      <p className="mt-3 text-sm text-muted-foreground"><C k="track_intro" /></p>

      <form onSubmit={submit} className="mt-8 grid gap-4 sm:grid-cols-[1fr_auto] sm:items-end" data-testid="track-form">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Order ID</Label>
            <Input data-testid="track-order-number" value={orderNumber} onChange={(e) => setOrderNumber(e.target.value)} placeholder={`${partner?.order_prefix || "DL"}-XXXXXXXX`} className="mt-1.5 rounded-none mono uppercase" />
          </div>
          <div>
            <Label className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Tracking PIN</Label>
            <Input data-testid="track-pin" value={pin} onChange={(e) => setPin(e.target.value)} placeholder="6-character PIN" className="mt-1.5 rounded-none mono uppercase" />
          </div>
        </div>
        <button data-testid="track-submit" disabled={loading} className="flex items-center justify-center gap-2 bg-foreground px-6 py-3 text-sm font-medium text-background transition-transform enabled:hover:-translate-y-0.5 disabled:opacity-40">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          {loading ? "Checking…" : "Track"}
        </button>
      </form>
      <SupportBox />

      {order && (
        <div className="mt-10" data-testid="track-result">
          <div className="mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Order {order.order_number}</div>

          <div className={`mt-4 flex items-center gap-3 border p-4 ${paid ? "border-green-600 bg-green-50 dark:bg-green-950/20" : failed ? "border-destructive" : "border-border"}`} data-testid="track-payment-status">
            {paid ? <CheckCircle2 className="h-5 w-5 text-green-600" /> : failed ? <AlertTriangle className="h-5 w-5 text-destructive" /> : confirming ? <Loader2 className="h-5 w-5 animate-spin" /> : <Clock className="h-5 w-5 text-muted-foreground" />}
            <span className="text-sm font-medium">Payment: {STATUS_LABEL[order.payment_status] || order.payment_status}</span>
          </div>

          <div className="mt-4 flex items-center gap-3 border border-border p-4" data-testid="track-fulfilment-status">
            <Truck className="h-5 w-5 text-muted-foreground" />
            <span className="text-sm font-medium">{FULFILMENT_LABEL[order.fulfillment_status] || order.fulfillment_status || "Preparing your order"}</span>
          </div>

          {order.tracking_number ? (
            <div className="mt-4 border border-border p-4" data-testid="track-number">
              <div className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Tracking number</div>
              <div className="mt-1 font-heading text-xl font-black mono">{order.tracking_number}</div>
            </div>
          ) : (
            <div className="mt-4 border border-dashed border-border p-4 text-sm text-muted-foreground" data-testid="track-number-pending">
              A tracking number will appear here once your order is dispatched. We update this daily — check back anytime.
            </div>
          )}

          <div className="mt-6 border border-border">
            <div className="mono border-b border-border bg-muted px-5 py-3 text-[10px] uppercase tracking-[0.2em]">Items</div>
            <div className="divide-y divide-border">
              {(order.items || []).map((it, i) => (
                <div key={`${it.type}-${i}`} className="flex justify-between p-4 text-sm">
                  <span>{orderItemLabel(it)}</span>
                  <span className="mono">{gbp(it.line_total)}</span>
                </div>
              ))}
              <div className="flex justify-between p-4 font-bold"><span>Total</span><span className="mono">{gbp(order.total)}</span></div>
            </div>
          </div>

          <button data-testid="track-reorder" onClick={reorder} disabled={reordering}
            className="mt-6 flex w-full items-center justify-center gap-2 bg-foreground py-4 text-sm font-medium text-background transition-transform enabled:hover:-translate-y-0.5 disabled:opacity-40">
            {reordering ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
            {reordering ? "Adding to basket…" : "Re-order this — same box at today's prices"}
          </button>
        </div>
      )}
    </div>
  );
}
